import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { CurrencyField } from '@pesto/ui/components/Forms/CurrencyField';
import { z } from 'zod';

import type { LoanModalPayload, ModalProps } from '../types/modal.types';

import { useUpdateCreditLimitMutation } from '../../__generated__/graphql/api';
import { getLoanRefetchQuery } from '../../components/LoanDetails/getLoanRefetchQuery';
import { createFormComponent } from '../utils/create-form-component';

const schema = z.object({
  creditLimit: z.number().min(1, 'Credit limit is required'),
  reason: z.string().min(1, 'Reason is required'),
});

export const UpdateCreditLimitComponent = ({ payload, api }: ModalProps<LoanModalPayload>) => {
  const { loanId } = payload;
  const { handleMutation } = useMutationWithToast();
  const refetchQuery = getLoanRefetchQuery(loanId);
  const [updateCreditLimit, { loading }] = useUpdateCreditLimitMutation(refetchQuery);

  const handleSubmit = (data: z.infer<typeof schema>) => {
    void handleMutation({
      mutation: () =>
        updateCreditLimit({
          variables: {
            loanId,
            creditLimit: parseInt(String(data.creditLimit)),
            reason: data.reason,
          },
          ...getLoanRefetchQuery(loanId),
        }),
      extractSuccess: (result) => !!result?.data,
      onSuccess: () => api.close(),
      debugLabel: 'update-credit-limit',
      toast: {
        success: 'Credit limit updated successfully',
        error: 'Failed to update credit limit',
        loading: 'Updating credit limit...',
      },
    });
  };

  const config: SmartFormConfig<z.infer<typeof schema>> = {
    fields: [
      {
        name: 'creditLimit',
        label: 'Credit Limit',
        required: true,
        defaultValue: 0,
        component: CurrencyField,
        componentProps: {
          showCurrencySymbol: true,
        },
      },
      {
        name: 'reason',
        label: 'Reason',
        type: 'text',
        required: true,
      },
    ],
    schema,
    defaultValues: {
      creditLimit: 0,
      reason: '',
    },
  };

  return createFormComponent({
    config,
    handleSubmit,
    loading,
  });
};
